import { useState, useMemo, useEffect, useRef } from "react";
import { MobileCalendar } from "@/components/calendar/mobile/MobileCalendar";
import { Button, FilterMenu, type FilterMenuGroup } from "@akuna/ui";
import { SearchField } from "@akuna/ui";
import { Plus, Settings, HelpCircle, LayoutGrid, Download, Play } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { AppointmentForm } from "@/components/calendar/AppointmentForm";
import { AppointmentFormResponsiveDialog } from "@/components/calendar/AppointmentFormResponsiveDialog";
import { QuickStartAttendanceDialog } from "@/components/calendar/QuickStartAttendanceDialog";
import { CalendarView } from "@/components/calendar/CalendarView";
import { useNavigate, useLocation } from "react-router-dom";
import { AppointmentDetailsDialog } from "@/components/calendar/AppointmentDetailsDialog";
import { fetchAppointmentsForCsv, useCurrentClinicId, useVeterinarians, useAppointments } from "@/hooks/useAppointments";
import { useAppointmentStatuses } from "@/hooks/useAppointmentStatuses";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { Checkbox } from "@akuna/ui";
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { PermissionGuard } from "@/components/PermissionGuard";
import {
  saveLastCalendarView,
} from "@/lib/utils/calendar-navigation";
import {
  persistCalendarFilters,
  readPersistedCalendarFilters,
} from "@/lib/utils/appointment-filters-storage";
import { formatLocalTimeHHmm } from "@/lib/date-utils";
import {
  APPOINTMENT_EXPORT_COLUMN_ORDER,
  type AppointmentExportColumn,
  DEFAULT_APPOINTMENT_EXPORT_COLUMNS,
  downloadAppointmentsCsv,
  getAppointmentsCsvFilename,
} from "@/lib/exportAppointmentsToCsv";
import { matchesOwnerPetSearch } from "@/lib/patients/search";
import { toast } from "@/hooks/use-toast";
import { useCanStartAttendance } from "@/hooks/useCanStartAttendance";
import { matchesAppointmentTypeFilter } from "@/lib/appointments/type";
import { getEffectiveAppointmentDriverType } from "@/lib/utils/appointment-driver";
import { BEHAVIOR_OPTIONS } from "@/lib/appointments/availability";
import { useTranslation } from "react-i18next";
import { useClinicFeatureFlags } from "@/hooks/useClinicFeatureFlags";
import { useClinicAppointmentTypes } from "@/hooks/useClinicAppointmentTypes";
import { getActiveClinicScope } from "@/lib/clinics/active-clinic-scope";
import {
  getEffectiveAppointmentStatusName,
} from "@/lib/utils/clinic-appointment-status";

type CalendarViewMode = "day" | "week" | "month" | "agenda";
type PersistedCalendarViewMode = Exclude<CalendarViewMode, "agenda">;

const CALENDAR_VIEW_MODE_STORAGE_KEY = "calendar-view-mode";
const APPOINTMENTS_EXPORT_COLUMNS_STORAGE_KEY = "appointments-export-columns-v1";
const DEFAULT_CALENDAR_VIEW_MODE: CalendarViewMode = "week";
const EXPORT_COLUMN_LABEL_KEYS: Record<AppointmentExportColumn, string> = {
  Data: "data",
  Hora: "hora",
  "Pet(s)": "pets",
  "Responsável": "responsavel",
  Endereço: "endereco",
  "Telefone(s)": "telefone",
  "E-mail": "email",
  Tipo: "tipo",
  Status: "status",
  "Produtos/Serviços": "produtosServicos",
  Pacotes: "pacotes",
  Motorista: "motorista",
  "Observações": "observacoes",
};

function readPersistedCalendarViewMode(): CalendarViewMode {
  try {
    const saved = localStorage.getItem(CALENDAR_VIEW_MODE_STORAGE_KEY);
    if (saved === "day" || saved === "week" || saved === "month") return saved;
  } catch {
    // Ignore (e.g. localStorage blocked)
  }
  return DEFAULT_CALENDAR_VIEW_MODE;
}

function isPersistedCalendarViewMode(value: CalendarViewMode): value is PersistedCalendarViewMode {
  return value === "day" || value === "week" || value === "month";
}

function isAppointmentExportColumn(value: string): value is AppointmentExportColumn {
  return (APPOINTMENT_EXPORT_COLUMN_ORDER as readonly string[]).includes(value);
}

function readPersistedExportColumns(): AppointmentExportColumn[] {
  try {
    const raw = localStorage.getItem(APPOINTMENTS_EXPORT_COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_APPOINTMENT_EXPORT_COLUMNS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_APPOINTMENT_EXPORT_COLUMNS;
    const columns = parsed.filter((value): value is AppointmentExportColumn =>
      typeof value === "string" && isAppointmentExportColumn(value),
    );
    return columns.length > 0 ? columns : DEFAULT_APPOINTMENT_EXPORT_COLUMNS;
  } catch {
    return DEFAULT_APPOINTMENT_EXPORT_COLUMNS;
  }
}

export default function Calendar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [viewMode, setViewMode] = useState<CalendarViewMode>(() => readPersistedCalendarViewMode());
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isQuickStartOpen, setIsQuickStartOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState<AppointmentExportColumn[]>(
    () => readPersistedExportColumns(),
  );
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const [prefilledDate, setPrefilledDate] = useState<Date | undefined>(undefined);
  const [prefilledTime, setPrefilledTime] = useState<string | undefined>(undefined);
  const [prefilledEndTime, setPrefilledEndTime] = useState<string | undefined>(undefined);
  const [prefilledOwnerId, setPrefilledOwnerId] = useState<string | undefined>(undefined);
  const [prefilledPetId, setPrefilledPetId] = useState<string | undefined>(undefined);
  const [prefilledVetId, setPrefilledVetId] = useState<string | undefined>(undefined);

  // Ref to track processed location.state to prevent reopening modal
  const processedStateRef = useRef<string | null>(null);
  // When a click that dismisses an open dialog lands on the calendar area,
  // react-big-calendar still fires an empty-slot selection on mouseup, which
  // would immediately open a new "create appointment" dialog. We detect this by
  // snapshotting, on pointerdown (before Radix closes the dialog), whether any
  // overlay was open — if so, that gesture must not create an appointment.
  const isOverlayOpenRef = useRef(false);
  const suppressSlotSelectRef = useRef(false);

  const initialCalendarFilters = useMemo(() => readPersistedCalendarFilters(), []);

  // Filters state (restored from localStorage once on mount)
  const [searchTerm, setSearchTerm] = useState(initialCalendarFilters.searchTerm);
  const [selectedVets, setSelectedVets] = useState<string[]>(initialCalendarFilters.selectedVets);
  const [selectedTypes, setSelectedTypes] = useState<string[]>(initialCalendarFilters.selectedTypes);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(initialCalendarFilters.selectedStatuses);
  const [selectedDriverTypes, setSelectedDriverTypes] = useState<string[]>(initialCalendarFilters.selectedDriverTypes);
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
  const canShowQuickStart = useCanStartAttendance();

  const {
    data: clinicId,
    isLoading: isClinicLoading,
    error: clinicError,
  } = useCurrentClinicId();

  const agendaClinicIds = useMemo(
    () => getActiveClinicScope(clinicId),
    [clinicId],
  );

  const { data: veterinarians = [] } = useVeterinarians(clinicId);
  const { data: appointmentStatusesData = [] } = useAppointmentStatuses("appointment");
  const { data: groomingStatusesData = [] } = useAppointmentStatuses("grooming");
  const { data: clinicAppointmentTypes = [] } = useClinicAppointmentTypes();

  const { rangeStart, rangeEnd } = useMemo(() => {
    if (viewMode === "day") {
      return {
        rangeStart: startOfDay(currentDate),
        rangeEnd: endOfDay(currentDate),
      };
    }

    if (viewMode === "month") {
      // Fetch a full visible month grid (incl. leading/trailing days in the first/last week).
      const start = startOfWeek(startOfMonth(currentDate), { locale: ptBR });
      const end = endOfWeek(endOfMonth(currentDate), { locale: ptBR });
      return {
        rangeStart: startOfDay(start),
        rangeEnd: endOfDay(end),
      };
    }

    // Default: week (also used as a fallback for "agenda")
    const start = startOfWeek(currentDate, { locale: ptBR });
    const end = endOfWeek(currentDate, { locale: ptBR });
    return {
      rangeStart: startOfDay(start),
      rangeEnd: endOfDay(end),
    };
  }, [currentDate, viewMode]);

  const { data: allAppointments = [] } = useAppointments(
    clinicId,
    undefined,
    rangeStart,
    rangeEnd,
    { variant: "list", clinicIds: agendaClinicIds },
  );
  const { data: clinicFeatureFlags } = useClinicFeatureFlags();

  const professionalFilterOptions = useMemo(() => {
    const professionalsById = new Map<string, { id: string; full_name: string }>();

    veterinarians.forEach((vet) => {
      if (!vet.id || !vet.full_name?.trim()) return;
      professionalsById.set(vet.id, { id: vet.id, full_name: vet.full_name.trim() });
    });

    allAppointments.forEach((appointment) => {
      const vetUserId = appointment.vet_user_id;
      if (!vetUserId || professionalsById.has(vetUserId)) return;

      const joinedName = (appointment.vet as { full_name?: string | null } | null | undefined)?.full_name?.trim();
      if (joinedName) {
        professionalsById.set(vetUserId, { id: vetUserId, full_name: joinedName });
      }
    });

    return Array.from(professionalsById.values()).sort((left, right) =>
      left.full_name.localeCompare(right.full_name, "pt-BR", { sensitivity: "base" }),
    );
  }, [allAppointments, veterinarians]);

  // Persist calendar view mode (day/week/month) in localStorage
  useEffect(() => {
    if (!isPersistedCalendarViewMode(viewMode)) return;
    try {
      localStorage.setItem(CALENDAR_VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      // Ignore (e.g. localStorage blocked)
    }
  }, [viewMode]);

  // Save current view as last visited calendar view
  useEffect(() => {
    saveLastCalendarView("calendar");
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        APPOINTMENTS_EXPORT_COLUMNS_STORAGE_KEY,
        JSON.stringify(selectedExportColumns),
      );
    } catch {
      // Ignore (e.g. localStorage blocked)
    }
  }, [selectedExportColumns]);

  useEffect(() => {
    persistCalendarFilters({
      searchTerm,
      selectedVets,
      selectedTypes,
      selectedStatuses,
      selectedDriverTypes,
    });
  }, [searchTerm, selectedVets, selectedTypes, selectedStatuses, selectedDriverTypes]);

  // Check if appointmentId was passed via location state (from vaccination link)
  useEffect(() => {
    const state = location.state as
      | {
        appointmentId?: string;
        ownerId?: string;
        petId?: string;
      }
      | null;

    if (!state) {
      processedStateRef.current = null;
      return;
    }

    // Create a unique key based on the state content
    const stateKey = JSON.stringify({
      appointmentId: state.appointmentId,
      ownerId: state.ownerId,
      petId: state.petId,
    });

    // Only process if it's a new state (not processed previously)
    if (processedStateRef.current === stateKey) {
      return;
    }

    if (state.appointmentId && !selectedAppointmentId) {
      setSelectedAppointmentId(state.appointmentId);
      processedStateRef.current = stateKey;
    }

    if ((state.ownerId || state.petId) && !isCreateOpen) {
      setPrefilledOwnerId(state.ownerId);
      setPrefilledPetId(state.petId);
      setIsCreateOpen(true);
      processedStateRef.current = stateKey;
    }

    // Clear the state from React Router using navigate
    if (state.appointmentId || state.ownerId || state.petId) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, selectedAppointmentId, isCreateOpen, navigate, location.pathname]);

  // Mirror "is any calendar-covering dialog open" into a ref so it can be read
  // synchronously from the pointerdown listener below.
  useEffect(() => {
    isOverlayOpenRef.current =
      isCreateOpen || isQuickStartOpen || isExportDialogOpen || selectedAppointmentId !== null;
  }, [isCreateOpen, isQuickStartOpen, isExportDialogOpen, selectedAppointmentId]);

  // On every pointerdown, snapshot whether an overlay was open at that instant —
  // this runs before Radix closes the dialog (which also happens on pointerdown)
  // and before react-big-calendar fires onSelectSlot (on the later mouseup).
  useEffect(() => {
    const handlePointerDown = () => {
      suppressSlotSelectRef.current = isOverlayOpenRef.current;
    };
    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, []);

  const handleCreateAppointment = (date: Date, startTime?: string, endTime?: string, vetUserId?: string | null) => {
    // If this same click just dismissed an open dialog (clicking outside it onto
    // the calendar), only dismiss it — don't open a new create dialog.
    if (suppressSlotSelectRef.current) {
      return;
    }
    setPrefilledDate(date);
    setPrefilledTime(startTime);
    setPrefilledEndTime(endTime);
    setPrefilledOwnerId(undefined);
    setPrefilledPetId(undefined);
    setPrefilledVetId(vetUserId ?? undefined);
    setIsCreateOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateOpen(false);
    processedStateRef.current = null; // Clear the ref when closing manually
    setTimeout(() => {
      setPrefilledDate(undefined);
      setPrefilledTime(undefined);
      setPrefilledEndTime(undefined);
      setPrefilledOwnerId(undefined);
      setPrefilledPetId(undefined);
      setPrefilledVetId(undefined);
    }, 300);
  };

  /** When opening "new appointment" without a slot or explicit date, default to today and current time. */
  const effectivePrefilledDate = useMemo(() => {
    if (!isCreateOpen) return prefilledDate;
    return prefilledDate ?? new Date();
  }, [isCreateOpen, prefilledDate]);

  const effectivePrefilledTime = useMemo(() => {
    if (!isCreateOpen) return prefilledTime;
    return prefilledTime ?? formatLocalTimeHHmm();
  }, [isCreateOpen, prefilledTime]);

  // Filter appointments on client side
  const filteredAppointments = useMemo(() => {
    return allAppointments.filter((appointment) => {
      // Filter by search term (pet name, owner name, or pet record number)
      if (searchTerm) {
        const ownerName = appointment.owner?.name;
        const pets = (appointment.pets ?? []).length > 0 ? appointment.pets ?? [] : [appointment.pet];
        const hasPetMatch = pets.some((pet) =>
          matchesOwnerPetSearch({
            term: searchTerm,
            ownerName: typeof ownerName === "string" ? ownerName : undefined,
            petName: typeof pet?.name === "string" ? pet.name : undefined,
            petRecordNumber: typeof pet?.record_number === "string" ? pet.record_number : undefined,
          }),
        );
        if (!hasPetMatch) {
          return false;
        }
      }

      // Filter by veterinarians
      if (selectedVets.length > 0 && appointment.vet_user_id) {
        if (!selectedVets.includes(appointment.vet_user_id)) {
          return false;
        }
      }

      // Filter by appointment types
      if (!matchesAppointmentTypeFilter(appointment.type, selectedTypes, appointment.clinic_appointment_type?.behavior, appointment.clinic_appointment_type?.id)) {
        return false;
      }

      // Filter by status
      if (selectedStatuses.length > 0) {
        const effectiveStatus = getEffectiveAppointmentStatusName(appointment);
        if (!selectedStatuses.includes(effectiveStatus)) {
          return false;
        }
      }

      // Filter by Táxi Dog (Buscar/Levar)
      if (selectedDriverTypes.length > 0) {
        const effectiveType = getEffectiveAppointmentDriverType({
          driverType: appointment.taxi_dog_type,
          isTaxiDog: appointment.is_taxi_dog,
        });
        const matchesDriverType = selectedDriverTypes.some(
          (driverType) => effectiveType === driverType || effectiveType === "pickup_and_dropoff"
        );
        if (!matchesDriverType) {
          return false;
        }
      }

      return true;
    });
  }, [allAppointments, searchTerm, selectedVets, selectedTypes, selectedStatuses, selectedDriverTypes]);

  const { t } = useTranslation("calendar");
  const appointmentTypes = useMemo(
    () =>
      clinicAppointmentTypes.length > 0
        ? clinicAppointmentTypes.map((type) => ({
            value: type.id,
            label: type.name,
            color: type.color,
            icon: type.icon,
          }))
        : BEHAVIOR_OPTIONS.map((option) => ({
            value: option.value,
            label: t(option.labelKey),
            color:
              option.value === "padrao" ? "#3b82f6" :
              option.value === "banho_tosa" ? "#22c55e" :
              option.value === "retorno" ? "#f59e0b" :
              option.value === "aplicacao" ? "#6366f1" :
              "#8b5cf6",
            icon: option.value,
          })),
    [clinicAppointmentTypes, t],
  );

  useEffect(() => {
    if (clinicAppointmentTypes.length === 0 || selectedTypes.length === 0) return;
    setSelectedTypes((prev) => {
      const migrated = prev.flatMap((value) => {
        const matchingTypeIds = clinicAppointmentTypes
          .filter((type) => type.behavior === value)
          .map((type) => type.id);
        return matchingTypeIds.length > 0 ? matchingTypeIds : [value];
      });
      const unique = Array.from(new Set<string>(migrated));
      return unique.length === prev.length && unique.every((value, index) => value === prev[index])
        ? prev
        : unique;
    });
  }, [clinicAppointmentTypes, selectedTypes.length]);

  const allStatusesRaw = useMemo(() => {
    const merged = [...appointmentStatusesData, ...groomingStatusesData];
    const seen = new Set<string>();
    return merged.filter((status) => {
      if (seen.has(status.name)) return false;
      seen.add(status.name);
      return true;
    });
  }, [appointmentStatusesData, groomingStatusesData]);

  const appointmentStatuses = useMemo(() =>
    allStatusesRaw.map((status) => ({
      value: status.name,
      label: status.display_name,
      scope: status.scope,
      color: status.color,
    })),
  [allStatusesRaw]);

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedVets([]);
    setSelectedTypes([]);
    setSelectedStatuses([]);
    setSelectedDriverTypes([]);
  };

  const toggleExportColumn = (column: AppointmentExportColumn) => {
    setSelectedExportColumns((prev) =>
      prev.includes(column)
        ? prev.filter((item) => item !== column)
        : APPOINTMENT_EXPORT_COLUMN_ORDER.filter((item) => [...prev, column].includes(item)),
    );
  };

  const handleConfirmExport = async () => {
    const csvView = viewMode === "agenda" ? "week" : viewMode;
    const filename = getAppointmentsCsvFilename({ view: csvView, date: currentDate });
    const effectiveColumns =
      selectedExportColumns.length > 0 ? selectedExportColumns : DEFAULT_APPOINTMENT_EXPORT_COLUMNS;

    setIsExporting(true);
    try {
      const detailed = await fetchAppointmentsForCsv({
        appointmentIds: filteredAppointments.map((a) => a.id),
      });
      downloadAppointmentsCsv(detailed, filename, effectiveColumns);
    } catch (err) {
      console.error(err);
      toast({
        title: t("exportErrorTitle"),
        description: t("exportErrorDescription"),
      });
      downloadAppointmentsCsv(filteredAppointments, filename, effectiveColumns);
    } finally {
      setIsExporting(false);
      setIsExportDialogOpen(false);
    }
  };

  const activeFiltersCount =
    (searchTerm ? 1 : 0) +
    selectedVets.length +
    selectedTypes.length +
    selectedStatuses.length +
    selectedDriverTypes.length;

  const calendarFilterGroups: FilterMenuGroup[] = [
    {
      id: "vets",
      title: t("filterByProfessional"),
      options: professionalFilterOptions.map((vet) => ({ label: vet.full_name, value: vet.id })),
      selected: selectedVets,
      onChange: setSelectedVets,
      emptyMessage: t("appointmentForm.noProfessionalResults"),
    },
    {
      id: "types",
      title: t("filterByType"),
      options: appointmentTypes.map((type) => ({ label: type.label, value: type.value })),
      selected: selectedTypes,
      onChange: setSelectedTypes,
    },
    {
      id: "statuses",
      title: t("filterByStatus"),
      options: appointmentStatuses.map((status) => ({ label: status.label, value: status.value })),
      selected: selectedStatuses,
      onChange: setSelectedStatuses,
    },
    {
      id: "driverTypes",
      title: t("filterByDriverType"),
      options: [
        { label: t("driverTypePickup"), value: "pickup" },
        { label: t("driverTypeDropoff"), value: "dropoff" },
      ],
      selected: selectedDriverTypes,
      onChange: setSelectedDriverTypes,
    },
  ];

  return (
    <PermissionGuard resource="appointments" action="view">
      <div className="space-y-2">
        <div className="hidden md:block">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t("title")}</h1>
          </div>
          <div className="flex flex-col md:flex-row gap-2 mt-0">

            {canShowQuickStart ? (
              <Button
                variant="outline"
                className="gap-2"
                disabled={isClinicLoading || !clinicId}
                onClick={() => setIsQuickStartOpen(true)}
              >
                <Play className="h-4 w-4" />
                {t("startAttendance")}
              </Button>
            ) : null}
              <Button className="gap-2" disabled={isClinicLoading || !clinicId} onClick={() => setIsCreateOpen(true)}>
                <Plus className="h-4 w-4" />
                {t("newAppointment")}
              </Button>
              <AppointmentFormResponsiveDialog
                open={isCreateOpen}
                onOpenChange={(open) => { if (!open) handleCloseCreateDialog(); else setIsCreateOpen(true); }}
                clinicId={clinicId}
                prefilledDate={effectivePrefilledDate}
                prefilledTime={effectivePrefilledTime}
                prefilledEndTime={prefilledEndTime}
                prefilledOwnerId={prefilledOwnerId}
                prefilledPetId={prefilledPetId}
                prefilledVetId={prefilledVetId}
                onSuccess={() => {
                  setIsCreateOpen(false);
                  processedStateRef.current = null;
                  setTimeout(() => {
                    setPrefilledDate(undefined);
                    setPrefilledTime(undefined);
                    setPrefilledEndTime(undefined);
                    setPrefilledOwnerId(undefined);
                    setPrefilledPetId(undefined);
                    setPrefilledVetId(undefined);
                  }, 300);
                }}
              />
            {clinicId ? (
              <QuickStartAttendanceDialog
                clinicId={clinicId}
                source="calendar"
                open={isQuickStartOpen}
                onOpenChange={setIsQuickStartOpen}
              />
            ) : null}
          </div>
        </div>

        {/* Filters Section */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-3 flex-wrap items-center">
            <div className="flex-1 min-w-[200px] max-w-md">
              <SearchField
                label={t("searchPlaceholder")}
                clearLabel={t("common:clearSearch")}
                placeholder={t("searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClear={() => setSearchTerm("")}
              />
            </div>

            <FilterMenu groups={calendarFilterGroups} label={t("filters")} />
            {activeFiltersCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                {t("clearFilters")} ({activeFiltersCount})
              </Button>
            )}
          </div>


          <div className="flex gap-2 align-end">
            <Dialog open={isExportDialogOpen} onOpenChange={setIsExportDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="gap-2"
                  disabled={filteredAppointments.length === 0}
                >
                  <Download className="h-4 w-4" />
                  {t("export")}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t("exportDialog.title")}</DialogTitle>
                  <DialogDescription>{t("exportDialog.description")}</DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
                  {APPOINTMENT_EXPORT_COLUMN_ORDER.map((column) => (
                    <label key={column} className="flex items-center gap-2 text-sm">
                      <Checkbox
                        checked={selectedExportColumns.includes(column)}
                        onCheckedChange={() => toggleExportColumn(column)}
                      />
                      <span>{t(`exportDialog.columns.${EXPORT_COLUMN_LABEL_KEYS[column]}`)}</span>
                    </label>
                  ))}
                </div>
                <DialogFooter className="gap-2 sm:justify-between">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => setSelectedExportColumns(DEFAULT_APPOINTMENT_EXPORT_COLUMNS)}
                  >
                    {t("exportDialog.reset")}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleConfirmExport}
                    disabled={isExporting}
                  >
                    {isExporting ? t("exportDialog.exporting") : t("exportDialog.confirm")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

          </div>
        </div>
        </div>{/* end hidden md:block desktop header */}

        {isClinicLoading && (
          <div className="space-y-4 rounded-lg border border-dashed p-6">
            <Skeleton className="h-6 w-1/3" />
            <Skeleton className="h-80 w-full" />
          </div>
        )}

        {clinicId && (
          <>
            <MobileCalendar
              className="md:hidden"
              appointments={filteredAppointments}
              professionals={veterinarians}
              statuses={allStatusesRaw}
              viewMode={viewMode === "agenda" ? "week" : viewMode}
              setViewMode={(v) => setViewMode(v)}
              currentDate={currentDate}
              onNavigate={setCurrentDate}
              onAppointmentClick={setSelectedAppointmentId}
              onCreateAppointment={() => setIsCreateOpen(true)}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              vetOptions={professionalFilterOptions.map((p) => ({ value: p.id, label: p.full_name }))}
              selectedVets={selectedVets}
              setSelectedVets={setSelectedVets}
              typeOptions={appointmentTypes.map((t) => ({ value: t.value, label: t.label }))}
              selectedTypes={selectedTypes}
              setSelectedTypes={setSelectedTypes}
              statusOptions={appointmentStatuses.map((s) => ({ value: s.value, label: s.label }))}
              selectedStatuses={selectedStatuses}
              setSelectedStatuses={setSelectedStatuses}
              onlyTaxiDog={selectedDriverTypes.length > 0}
              setOnlyTaxiDog={(value: boolean) => setSelectedDriverTypes(value ? ["pickup", "dropoff"] : [])}
            />
            <div className="hidden md:block">
              <CalendarView
                viewMode={viewMode}
                setViewMode={setViewMode}
                clinicId={clinicId}
                appointments={filteredAppointments}
                professionals={veterinarians}
                showAppointmentItemsOnCards={clinicFeatureFlags?.showAppointmentItemsOnCards === true}
                currentDate={currentDate}
                onNavigate={setCurrentDate}
                onAppointmentClick={setSelectedAppointmentId}
                onCreateAppointment={handleCreateAppointment}
              />
            </div>

            {selectedAppointmentId && (
              <AppointmentDetailsDialog
                appointmentId={selectedAppointmentId}
                open={!!selectedAppointmentId}
                onOpenChange={(open) => {
                  if (!open) {
                    setSelectedAppointmentId(null);
                  }
                }}
              />
            )}
          </>
        )}
      </div>
    </PermissionGuard>
  );
}
